import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Camera, Palette, Film, Shield, Users, Award } from 'lucide-react';
import ArtworkCard from '../components/ArtworkCard';
import { API } from '../config/api';

const categories = [
  { id: 'photography', label: 'Photography', icon: Camera },
  { id: 'fine_art', label: 'Fine Art', icon: Palette },
  { id: 'film', label: 'Film', icon: Film },
];

export default function Home() {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeatured = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artworks/featured`);
      setFeaturedArtworks(response.data);
    } catch (error) {
      console.error('Error fetching featured:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div 
          className="hero-background"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1630182044473-34dd20c6dd8c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHBhaW50aW5nJTIwdGV4dHVyZSUyMGRhcmt8ZW58MHx8fHwxNzc1MjA1MDIxfDA&ixlib=rb-4.1.0&q=85)` 
          }}
        />
        <div className="hero-overlay" />
        
        <div className="hero-content animate-fade-in">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-6">
            100% Human-Created
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-[#F5F5F0] leading-tight">
            Art by Mortals.<br />
            <span className="text-[#D4AF37]">For Mortals.</span>
          </h1>
          <p className="mt-6 text-lg text-[#A1A1AA] max-w-lg mx-auto">
            A sanctuary for genuine human creativity. Photography, fine art, 
            and films — no AI-generated content, ever.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/gallery"
              className="btn-primary flex items-center gap-2"
              data-testid="explore-gallery-btn"
            >
              Explore Gallery
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/register"
              className="btn-secondary"
              data-testid="join-as-artist-btn"
            >
              Join as Artist
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Browse By</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-[#F5F5F0]">
              Categories
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/gallery/${category.id}`}
                className="group p-6 bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-300 text-center"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`category-${category.id}`}
              >
                <category.icon 
                  size={32} 
                  className="mx-auto text-[#71717A] group-hover:text-[#D4AF37] transition-colors" 
                />
                <p className="mt-4 text-sm text-[#A1A1AA] group-hover:text-[#F5F5F0] transition-colors">
                  {category.label}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Artworks */}
      {featuredArtworks.length > 0 && (
        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Discover</p>
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-[#F5F5F0]">
                  Featured Works
                </h2>
              </div>
              <Link
                to="/gallery"
                className="hidden sm:flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
              >
                View All
                <ArrowRight size={16} />
              </Link>
            </div>
            
            <div className="bento-grid">
              {featuredArtworks.slice(0, 6).map((artwork, index) => (
                <ArtworkCard 
                  key={artwork.id} 
                  artwork={artwork} 
                  featured={index === 0}
                />
              ))}
            </div>
            
            <div className="sm:hidden mt-8 text-center">
              <Link
                to="/gallery"
                className="btn-secondary inline-flex items-center gap-2"
              >
                View All Works
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Value Props */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <div className="text-center p-8">
              <Shield className="mx-auto text-[#D4AF37] mb-6" size={40} />
              <h3 className="font-serif text-xl text-[#F5F5F0] mb-4">Protected Content</h3>
              <p className="text-[#71717A]">
                Advanced digital protection ensures your artwork stays secure with 
                watermarks, disabled right-click, and low-resolution previews.
              </p>
            </div>
            
            <div className="text-center p-8">
              <Users className="mx-auto text-[#D4AF37] mb-6" size={40} />
              <h3 className="font-serif text-xl text-[#F5F5F0] mb-4">Authentic Artists</h3>
              <p className="text-[#71717A]">
                Every piece is verified human-created. We strictly prohibit 
                AI-generated content to preserve the integrity of true artistry.
              </p>
            </div>
            
            <div className="text-center p-8">
              <Award className="mx-auto text-[#D4AF37] mb-6" size={40} />
              <h3 className="font-serif text-xl text-[#F5F5F0] mb-4">Fair Commission</h3>
              <p className="text-[#71717A]">
                Artists keep 82% of every sale. Low listing fees and transparent 
                pricing mean more earnings in your pocket.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#121212]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0]">
            Ready to Share Your Art?
          </h2>
          <p className="mt-6 text-lg text-[#A1A1AA]">
            Join our community of artists and reach collectors who appreciate 
            authentic human creativity.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="btn-primary"
              data-testid="cta-artist-btn"
            >
              Start Selling Today
            </Link>
            <Link
              to="/membership"
              className="btn-secondary"
              data-testid="cta-collector-btn"
            >
              Become a Collector
            </Link>
          </div>
          <p className="mt-8 text-sm text-[#71717A]">
            $3 listing fee • 18% commission • 30-day featured placement
          </p>
        </div>
      </section>
    </div>
  );
}
